import * as React from "react";
import {authorize, IWithRouterProps, Overlay, withRouter} from "@athena-portal/components";
import {PluginTemplates} from "../../../../../utils/PluginTemplates";
import * as moment from "moment";
import {
    CAR_CERTIFICATE_FILENAME,
    CAR_WOLF_RENEWAL_NCD_PROOF,
    CommonHelpers,
    Document,
    FalconNavigatorHeader,
    FREETEXT_DOCUMENT,
    ICustomerPortalService,
    InteractionHelper,
    NON_PRINTABLE_DOCUMENTS,
    Policy,
    POLICY_STATUS_PENDING_RENEWAL,
    PRODUCT_WOLF_MOTOR,
    productHelper,
    ProductsEnum,
    Renewal,
    Types,
    WELCOME_PACK,
    WOLF_CANCELLATION_EMAIL_TITLE,
    WOLF_CHARGEBACK_CANCELLATION,
    WOLF_CHARGEBACK_COVER,
    WOLF_CHARGEBACK_NO_COVER,
    WOLF_MTA_CONFIRMATION_PACK,
    WOLF_RENEWAL_ACCEPTANCE,
    WOLF_RENEWAL_CLAIMS_IMPACT,
    WOLF_RENEWAL_INVITE_PACK,
    Sms,
    CoverChange,
    OptionalExtras
} from "@falcon/services";
import {lazyInject} from "@athena-portal/core";
import {finalize, map} from "rxjs/operators";
import FalconHeader from "../../../../../components/navigation/FalconHeader";
import FalconFooter from "../../../../../components/navigation/FalconFooter";
import {downloadDocument, trimFileExtension} from "../../../../../CommonFunctions";
import {FalconAccordion} from "../../../../../components/FalconAccordion";
import {mapCommunicationTitles, mapDocumentTitles} from "../../../../../utils/DataMappers";
import {RenewalDocumentsBanner} from "../../../../../components/banners/RenewalDocumentsBanner";
import {PostRequestedDocumentsButton} from "./PostRequestedDocumentsButton";
import {DocumentListModal, latestCoverChangeDate} from "@falcon/plugin-falcon-journey";
import {AllianzOnlineBadge} from "../../../../../components/AllianzOnlineBadge";
import { notificationTemplates } from "../../../common/documents/NotificationTemplates";
import CommunicationList from "../../../common/documents/CommunicationList";
import {CommunicationType} from "../../../common/documents/CommunicationType";

interface IDocumentsPageParams {
    policyNumber: string;
    [key: string]: string | undefined
}

interface IState {
    policies?: Policy[];
    carsDocuments: Document[];
    archiveDocuments: Document[];
    archivedDocumentsElementsByDate: React.ReactNode[];
    carsRenewalDocuments: Document[];
    communications: Document[];
    direction: string;
    active: boolean;
    isWhite: boolean;
    day: number;
    isDocumentDownloaded: boolean;
    receiveByPostChecked: boolean | null;
    renewalProposal?: Renewal;
    isRenewed?: boolean;
    showLoading: boolean;
    lastYearsPolicy?: Policy;
    emails: Document[];
    sms: Sms[];
    addedMLEDate?: Date;
    addedBDIDate?: Date;
}

@authorize
export class AnnualDocumentsPagePlain extends React.Component<IWithRouterProps<IDocumentsPageParams>, IState> {

    private pluginTemplates = new PluginTemplates();

    @lazyInject(Types.CustomerPortalService)
    private customerPortalService: ICustomerPortalService;

    private readonly FILE_PATH: string = "path";
    private readonly FILE_NAME: string = "fileName";
    private readonly FILE_ID: string = "id";
    private readonly FILE_LABEL: string = "label";
    private readonly isBritanniaBrand: boolean = ["bn", "britannia"].includes(
        (process.env.REACT_APP_THEME ?? "").toLowerCase()
    );

    constructor(props: Readonly<IWithRouterProps<IDocumentsPageParams>>) {
        super(props);
        this.state = {
            direction: 'asc',
            carsDocuments: [],
            carsRenewalDocuments: [],
            archiveDocuments: [],
            archivedDocumentsElementsByDate: [],
            communications: [],
            active: false,
            isWhite: true,
            day: 0,
            isDocumentDownloaded: true,
            receiveByPostChecked: null,
            renewalProposal: undefined,
            showLoading: true,
            lastYearsPolicy: undefined,
            emails: [],
            sms: []
        };

        this.sortByDate = this.sortByDate.bind(this);
        this.renderDocumentsByPost = this.renderDocumentsByPost.bind(this);
        this.showMore = this.showMore.bind(this);
        this.createPDFMap = this.createPDFMap.bind(this);

    }

    public componentDidMount(): void {
        this.pluginTemplates.setPageTitle("Policy documents");

        this.customerPortalService
            .getPolicies()
            .pipe(map((policies: Policy[]) => policies.filter((policy: Policy) => !policy.endDate || moment(policy.endDate).isSameOrAfter(moment().startOf("day")))))
            .subscribe((activePolicies: Policy[]) => {
                this.customerPortalService
                    .getExpiredPolicies(ProductsEnum.WOLF_MOTOR)
                    .subscribe(expiredPolicies => {
                        const filteredActivePolicies: Policy[] = activePolicies
                            .filter((policy: Policy) => policy.policyNumber === this.props.params.policyNumber)
                            .sort((a, b) => moment(b.endDate).diff(a.endDate));

                        const policies = [...(filteredActivePolicies || []), ...(expiredPolicies || [])];

                        this.setState({
                            policies
                        });
                        this.customerPortalService.getAllDocuments().subscribe(
                            (documents: Document[]) => {
                                this.customerPortalService.getRenewalProposal(this.props.params.policyNumber).subscribe(
                                    (renewalProposal: Renewal) => {
                                        this.setState({renewalProposal});
                                        this.fetchDocuments(policies, renewalProposal, documents);
                                    });
                                this.fetchCommunications(policies, documents);
                            });

                        if (!this.isBritanniaBrand) {
                            this.customerPortalService.getSms(this.props.params.policyNumber)
                                .subscribe((sms: Sms[]) => {
                                    this.setState({sms});
                                });

                            this.customerPortalService
                                .getOptionalExtrasInformation(this.props.params.policyNumber)
                                .subscribe(
                                    (optionalExtraChanges: CoverChange[]) => {
                                        this.setState({
                                            addedMLEDate: latestCoverChangeDate(optionalExtraChanges, OptionalExtras.LEGAL_EXPENSES),
                                            addedBDIDate: latestCoverChangeDate(optionalExtraChanges, [OptionalExtras.BREAKDOWN_COVER_LOCAL, OptionalExtras.BREAKDOWN_COVER_UK, OptionalExtras.BREAKDOWN_COVER_EU])
                                        });
                                    }
                                );
                        }

                        this.customerPortalService.getPolicyHistoryAfterRenewal()
                            .pipe(finalize(() => {
                                this.setState({ showLoading: false });
                            }))
                            .subscribe((policyHistories: Policy[]) => {
                                const lastYearsPolicy = this.getLastPolicy(policyHistories);
                                this.setState({
                                    isRenewed: !!lastYearsPolicy,
                                    ...(lastYearsPolicy ? { lastYearsPolicy } : {})
                                });
                            });
                    });
            });
    }

    public render() {
        const hasPendingRenewal: boolean = this.state.renewalProposal != null && this.state.renewalProposal.status === POLICY_STATUS_PENDING_RENEWAL;

        return (
            <React.Fragment>
                <FalconHeader/>

                <div className="plugin">
                    <AllianzOnlineBadge/>
                    <FalconNavigatorHeader
                        back={this.state.policies && `/dashboard/annualMotor/${this.state.policies[0].policyNumber}`}
                        title={this.isBritanniaBrand ? "Documents" : "Your car insurance policy documents and communications"}
                    />
                    {!this.state.showLoading && <div className="documents-page">
                        {hasPendingRenewal && <RenewalDocumentsBanner/>}
                        {hasPendingRenewal && this.renderRenewalDocumentsPanel()}
                        {hasPendingRenewal && this.renderExistingDocumentsPanel(false)}
                        {this.state.isRenewed && this.renderExistingDocumentsPanel(true)}
                        {!hasPendingRenewal && !this.state.isRenewed && this.renderDocumentsPanel()}

                        {this.isBritanniaBrand
                            ? this.renderDocumentsArchivePanel()
                            : (this.state.isRenewed && <CommunicationList
                                communicationCollection={{
                                    type: CommunicationType.ArchivedDocuments,
                                    items: this.state.archiveDocuments
                                }}
                                header={"Documents archive"}
                                cssStyles={"document-list documents-archive"}
                                navigate={this.props.navigate}
                            />)}
                        {this.renderDocumentsByPost()}
                        {!this.isBritanniaBrand && <CommunicationList
                            communicationCollection={{
                                type: CommunicationType.Emails,
                                items: this.state.emails
                            }}
                            header={"Emails"}
                            cssStyles={`document-list emails-block ${this.state.sms.length ? "" : "final-communication-block"}`}
                            navigate={this.props.navigate}
                        />}
                        {!this.isBritanniaBrand && <CommunicationList
                            communicationCollection={{
                                type: CommunicationType.SMS,
                                items: this.state.sms
                            }}
                            header={"SMS"}
                            cssStyles={"document-list sms-block final-communication-block"}
                            navigate={this.props.navigate}
                        />}
                    </div>}
                    {this.state.policies && this.pluginTemplates.renderBackLink("Back", false, `/dashboard/annualMotor/${this.state.policies[0].policyNumber}`)}
                </div>
                <Overlay show={this.state.showLoading}/>
                <FalconFooter/>
            </React.Fragment>
        );
    }

    private renderDocumentsArchivePanel(): React.ReactNode {
        if (this.state.archiveDocuments && this.state.archiveDocuments.length !== 0) {
            return (
                <div
                    className="widget documents-widget panel documents-archive">
                    <p className={"documents-archive-title font-weight-bold text-center"}>Documents archive</p>
                    <table>
                        <tbody>
                        {this.renderTableHeader()}
                        {this.state.archivedDocumentsElementsByDate}
                        </tbody>
                    </table>
                    {this.renderLoadMoreButton()}
                </div>
            );
        } else {
            return <></>;
        }
    }

    private getLastPolicy(policyHistories: Policy[]): Policy | null {
       const filteredPolicyHistories: Policy[] = policyHistories
            .filter((policy: Policy): boolean => policy.interaction.product === PRODUCT_WOLF_MOTOR);

        if (!filteredPolicyHistories?.length) { return null; }

        return filteredPolicyHistories.reduce(
            (latestSoFar, current) =>
                new Date(current.endDate) > new Date(latestSoFar.endDate)
                    ? current
                    : latestSoFar,
            policyHistories[0]
        );
    }

    private renderDocumentsByPost() {
        return (
            <div className="receive-by-post">
                <p className="receive-by-post-title font-weight-bold fs-18">Receive my policy documents by post</p>
                <p className="receive-by-post-description">We've gone paperless; we care about the environment and it's
                    better for everyone! But if you wish,
                    you can request below to have your documents posted to you to the address listed on your policy</p>
                {(this.state.carsDocuments || this.state.carsRenewalDocuments) && this.state.policies &&
                    <PostRequestedDocumentsButton
                        policy={this.state.policies[0]}
                        allDocuments={CommonHelpers.getPrintableDocuments(
                            this.state.carsDocuments.concat(this.state.carsRenewalDocuments), NON_PRINTABLE_DOCUMENTS
                        )}
                    />}
            </div>
        )
    }

    private renderDocumentsPanel(): React.ReactNode {
        if (this.state.policies){
            return (
                <div className="widget documents-widget active-policy-documents-widget panel">
                    <img className="mr-2 wolf-documents-page-icon mb-3" alt="document-icon" src="/img/wolf-your-car.png"/>
                    <p className="active-policy-documents-title font-weight-bold text-center fs-18">Policy documents</p>
                    <ul className="list-unstyled">
                        {this.state.communications.length > 0 && this.renderDocuments(this.state.communications)}
                        {this.renderDocuments(this.state.carsDocuments)}
                        <FalconAccordion toggleHidden={<div className="toggle">+ Show more</div>}
                                         toggleShown={<div className="toggle">- Show less</div>}>
                            <DocumentListModal
                                productName={productHelper.parseProductName(this.state.policies[0]?.products[0])}
                                policyDate={InteractionHelper.getPolicyDateForDocumentsFromPolicy(this.state.policies[0])}
                                isBDIVisible={this.state.policies[0].interaction.optionalExtras.breakdownCover}
                                isMLEVisible={this.state.policies[0].interaction.optionalExtras.motorLegalExpenses}
                                addedMLEDate={this.state.addedMLEDate}
                                addedBDIDate={this.state.addedBDIDate}
                                isAccordion={false}
                                coverTier={this.state.policies[0].interaction.coverTier}
                            />
                        </FalconAccordion>
                    </ul>
                </div>
            );
        } else {
            return <></>;
        }
    }

    private renderExistingDocumentsPanel(isShown: boolean): React.ReactNode {
       const cssClass =  this.state.isRenewed ? "existing-documents-widget-pending-renewal" : "existing-documents-widget";
       if(this.state.policies){
           return (
                <div className={`widget documents-widget panel ${cssClass}`}>
                    <ul className="list-unstyled">
                        <FalconAccordion
                            togglePositionTop={true}
                            isShown={isShown}
                            toggleShown={<div className="existing-documents-title toggle d-flex justify-content-between align-items-center accordion-collapsed"><span/>
                                <span className="font-weight-bold text-center fs-18">Existing policy documents</span>
                                <span className="toggle-icon collapse-icon"/></div>}
                            toggleHidden={<div className="existing-documents-title toggle d-flex justify-content-between align-items-center accordion-expanded"><span/>
                                <span className="font-weight-bold text-center fs-18">Existing policy documents</span>
                                <span className="toggle-icon expand-icon"/></div>}>
                            {this.state.communications.length > 0 && this.renderDocuments(this.state.communications)}
                            {this.renderDocuments(this.state.carsDocuments)}
                            <DocumentListModal
                                productName={productHelper.parseProductName(this.state.policies[0]?.products[0])}
                                policyDate={InteractionHelper.getPolicyDateForDocumentsFromPolicy(this.state.policies[0])}
                                isBDIVisible={this.state.policies[0].interaction.optionalExtras.breakdownCover}
                                isMLEVisible={this.state.policies[0].interaction.optionalExtras.motorLegalExpenses}
                                addedMLEDate={this.state.addedMLEDate}
                                addedBDIDate={this.state.addedBDIDate}
                                isAccordion={false}
                                coverTier={this.state.policies[0].interaction.coverTier}
                            />
                        </FalconAccordion>
                    </ul>
                </div>
            );
       } else {
           return <></>;
       }
    }

    private renderRenewalDocumentsPanel(): React.ReactNode {
        if (this.state.policies && this.state.renewalProposal){
            return (
                <div className="widget documents-widget renewal-documents-widget panel">
                    <ul className="list-unstyled">
                        <FalconAccordion isShown={true} togglePositionTop={true} toggleHidden={<div
                            className="renewal-documents-title toggle d-flex justify-content-between align-items-center accordion-collapsed"><span/><span
                            className="font-weight-bold text-center fs-18">Renewal policy documents</span><span
                            className="toggle-icon expand-icon"/></div>}
                                         toggleShown={<div
                                             className="renewal-documents-title toggle d-flex justify-content-between align-items-center accordion-expanded">
                                             <span/><span
                                             className="font-weight-bold text-center fs-18">Renewal policy documents</span><span
                                             className="toggle-icon collapse-icon"/></div>}>
                            {this.state.communications.length > 0 && this.renderDocuments(this.state.communications)}
                            {this.renderDocuments(this.state.carsRenewalDocuments)}
                            <DocumentListModal
                                productName={productHelper.parseProductName(this.state.policies[0]?.products[0])}
                                policyDate={InteractionHelper.getPolicyDateForDocumentsFromInteraction(this.state.renewalProposal)}
                                isBDIVisible={this.state.renewalProposal.optionalExtras.breakdownCover}
                                isMLEVisible={this.state.renewalProposal.optionalExtras.motorLegalExpenses}
                                addedMLEDate={this.state.addedMLEDate}
                                addedBDIDate={this.state.addedBDIDate}
                                isAccordion={true}
                                coverTier={this.state.renewalProposal.coverTier}
                            />
                        </FalconAccordion>
                    </ul>
                </div>
            );
        } else {
            return <></>;
        }
    }

    private renderDocuments(documents: Document[]): React.ReactNode[] {
        return documents.map((document: Document, index: number) => (
            <li className="media" key={index}>
                <a className="document-download-link no-underline" onClick={this.downloadDocument(document)} id={trimFileExtension(document.name)}> {/* NOSONAR */}
                    <img className="pdf-icon active-icon" alt="Document download icon"
                        src="/img/pdf-icon.png"/>
                    <h5 className="document-name d-flex document-link-text">{document.title}</h5>
                </a>
            </li>
        ));
    }

    private renderTableHeader(): React.ReactNode {
        return (
            <tr>
                <th className={"pb-3 pl-3"}>
                    <span className="documents-archive-table-header muli-semi-bold-16">Date</span>
                    <span onClick={this.sortByDate} className={`${this.state.active &&
                    (this.state.direction === "asc" ? "fas fa-sort-up" : "fas fa-sort-down")}
                        ${!this.state.active && "fas fa-sort"} table-sort-arrow`}/>
                </th>
            </tr>
        )
    }

    private renderLoadMoreButton(): React.ReactNode {
        if (!this.state.day) {
            return <></>;
        }
        if (this.state.day >= 4) {
            return (
                <button type="button" className="btn btn-primary btn-next plugin"
                        onMouseDown={this.showMore}
                        onTouchStart={this.showMore}
                        id={"show-more"}>
                    Load more
                </button>
            )
        }

        return <></>;
    }

    private readonly fetchDocuments = (policies?: Policy[], renewalProposal?: Renewal, documents?: Document[]) => {
        if (policies && policies.length > 0 && documents) {
            const carsRenewalDocuments: Document[] = [];
            const carsDocuments: Document[] = [];
            const archiveDocuments: Document[] = [];
            const ncdProofDocs: Document[] = [];
            let emails: Document[] = [];
            let hasActivePolicies: boolean = false;
            policies.forEach((policy: Policy) => {

                const allPolicyDocs = documents.filter(doc =>
                    doc.tags.includes(`agreement_${policy.policyNumber}`));

                const renewalDocuments =
                    this.getRenewalDocumentsForPolicy(allPolicyDocs, renewalProposal);

                carsRenewalDocuments.push(...mapDocumentTitles(renewalDocuments.currentRenewalDocuments, policy));
                archiveDocuments.push(...mapDocumentTitles(renewalDocuments.archivedRenewalInvites, policy));

                const currentPolicyVersionDocs = allPolicyDocs.filter(doc => {
                    return doc.tags.filter((d: string) => d === ("agreement_version_" + policy.interaction.agreementVersion)).length > 0
                });

                const documentsToBeListed = currentPolicyVersionDocs.filter(doc =>
                    doc.name.includes(CAR_CERTIFICATE_FILENAME)
                    || doc.name.includes(WOLF_RENEWAL_ACCEPTANCE)
                    || doc.name.includes(WELCOME_PACK)
                    || doc.name.includes(WOLF_MTA_CONFIRMATION_PACK));

                if (!hasActivePolicies && !productHelper.isActivePolicy(policy)) {
                    const policyNcdProofDoc = allPolicyDocs.filter(doc => doc.name.includes(CAR_WOLF_RENEWAL_NCD_PROOF + ".pdf"));

                    if (policyNcdProofDoc) {
                        ncdProofDocs.push(...mapDocumentTitles(policyNcdProofDoc, policy));
                    }
                } else {
                    hasActivePolicies = true;
                }

                const mappedCurrentDocuments = mapDocumentTitles(documentsToBeListed, policy);
                if (productHelper.includesWolfMotorProduct([policy])) {
                    carsDocuments.push(...mappedCurrentDocuments);
                }

                const documentsToBeListedWithoutAgreementVersion = allPolicyDocs.filter(doc =>
                    (doc.name.includes(CAR_CERTIFICATE_FILENAME)
                    || doc.name.includes(WELCOME_PACK)
                    || doc.name.includes(WOLF_RENEWAL_ACCEPTANCE)
                    || doc.name.includes(WOLF_MTA_CONFIRMATION_PACK)
                    || doc.name.includes(WOLF_CHARGEBACK_COVER)
                    || doc.name.includes(WOLF_CHARGEBACK_NO_COVER)
                    || doc.name.includes(WOLF_CHARGEBACK_CANCELLATION)
                    || doc.name.includes(FREETEXT_DOCUMENT)
                    )
                    && !doc.tags.includes(`agreement_version_${policy.interaction.agreementVersion}`)
                );

                if (productHelper.includesWolfMotorProduct([policy])) {
                    archiveDocuments.push(...mapDocumentTitles(documentsToBeListedWithoutAgreementVersion.reverse(), policy));
                }

                emails = allPolicyDocs.filter(document => notificationTemplates.emails.find(email => {
                        const isTemplateMatched: boolean = document.name === email.template;
                        if (isTemplateMatched) {
                            document.title = email.description;
                        }

                        return isTemplateMatched
                    })
                );
            });

            const selectedPolicy = policies.find(policy =>
                policy.policyNumber === this.props.params.policyNumber
            );

            if (selectedPolicy?.status === "RENEWAL") {
                const currentVersionTag = `agreement_version_${selectedPolicy.interaction.agreementVersion}`;

                const archivedDocumentIds = new Set(
                    archiveDocuments.map(document => document.id)
                );

                const missingArchiveDocuments = documents.filter(document => {
                    const belongsToPolicy = document.tags.includes(
                        `agreement_${selectedPolicy.policyNumber}`
                    );

                    const belongsToPreviousVersion = document.tags.some(tag =>
                            tag.startsWith("agreement_version_")
                        )
                        && !document.tags.includes(currentVersionTag);

                    const belongsToRenewal = document.tags.some(tag =>
                            /^renewal_[a-fA-F0-9]{24}$/.test(tag)
                        );

                    const isEmail = document.tags.some(tag =>
                        tag.startsWith("thunderhead_to_email_")
                    );

                    return belongsToPolicy && !isEmail && !archivedDocumentIds.has(document.id)
                        && (
                            belongsToPreviousVersion
                            || belongsToRenewal
                        );
                });

                archiveDocuments.push(
                    ...mapDocumentTitles(
                        missingArchiveDocuments,
                        selectedPolicy
                    )
                );
            }

            if (!hasActivePolicies && ncdProofDocs && ncdProofDocs.length > 0) {
                let latestNcdProofDoc = ncdProofDocs.sort((a, b) => moment(b.date).diff(moment(a.date)))[0];
                carsDocuments.push(latestNcdProofDoc);
            }

            this.sortByDescendingId(carsRenewalDocuments);
            this.sortByDescendingId(carsDocuments);
            this.sortByDescendingId(archiveDocuments);

            const archivedDocumentsElementsByDate = this.getArchivedDocumentsByDate(archiveDocuments, false);

            this.setState({
                carsRenewalDocuments,
                carsDocuments,
                archiveDocuments,
                archivedDocumentsElementsByDate,
                emails
            })
        }
    };

    private fetchCommunications = (policies?: Policy[], documents?: Document[]) => {
        if (policies && documents) {
            const communications: Document[] = [];
            const mtcPolicy = policies.find((policy: Policy) => policy.status.match(/mtc/i) && productHelper.isWolfMotor(policy.interaction));
            if (mtcPolicy) {
                const mtcDocs = documents.filter(doc => doc.name.includes(WOLF_CANCELLATION_EMAIL_TITLE));
                communications.push(...mapCommunicationTitles(mtcDocs, mtcPolicy));
            }
            this.setState({communications});
        }
    }

    private downloadDocument = (doc: Document) => (event: any): void => {
        this.setState({isDocumentDownloaded: false});
        this.customerPortalService.downloadDocument(doc.id).subscribe(
            (documentContent: ArrayBuffer) => {
                if (documentContent) {
                    downloadDocument(documentContent, doc.downloadName);
                }
                this.setState({isDocumentDownloaded: true});
            }
        );
    };

    private sortByDate(): void {
        const sortedDates = this.state.direction === 'asc' ?
            this.state.archiveDocuments!.sort((a, b) => moment(a.date).diff(b.date))
            :
            this.state.archiveDocuments!.sort((a, b) => moment(b.date).diff(a.date));
        this.setState({
            archivedDocumentsElementsByDate: this.getArchivedDocumentsByDate(sortedDates, false),
            direction: this.state.direction === 'asc' ? 'desc' : 'asc',
            active: true
        });
    }

    private sortByDescendingId(docs: Document[]): void {
        docs.sort((a, b) => Number(b.id) - Number(a.id));
    }

    private showMore(): void {
        this.setState({
            archivedDocumentsElementsByDate: this.getArchivedDocumentsByDate(this.state.archiveDocuments, true)
        })
    }

    private createPDFMap(path: string, fileName: string, id: string, label: string): Map<string, string> {
        return new Map()
            .set(this.FILE_PATH, path)
            .set(this.FILE_NAME, fileName)
            .set(this.FILE_ID, id)
            .set(this.FILE_LABEL, label);
    }

    private getArchivedDocumentsByDate(archivedDocuments: Document[], showHistory: boolean): React.ReactNode[] {
        let documents: React.ReactNode[];
        let day: number = 0;
        if (archivedDocuments && archivedDocuments.length !== 0) {
            let previousDate: string | null = "";
            let isWhite: boolean = true;
            documents = archivedDocuments.map((document, index) => {
                const {date, title, name} = document;
                if (PluginTemplates.convertDate(date, "DD MMMM YYYY") === previousDate) {
                    const backGround: string = isWhite ? "" : "light-blue-background";
                    const history: string = day >= 4 ? "display-none" : "";
                    return (
                        <tr key={index}
                            className={`${backGround} ${history} ${showHistory ? "display-block" : ""}`}>
                            <td id="document-date" className="muli-semi-bold-16">
                                <div className={"row ml-2"}>
                                    <h5 className={"col-10 pt-3 pl-0"}
                                        onClick={this.downloadDocument(document)}>{title}</h5>
                                    <a className="col-2 pl-1 document-download-link document-download-link-documents-page"
                                       onClick={this.downloadDocument(document)} id={trimFileExtension(name)}>
                                        <img className="active-policy-download-icon" alt="Document download icon"
                                             src="/img/Flow-download.svg"/>
                                    </a>
                                </div>
                            </td>
                        </tr>
                    )
                } else {
                    previousDate = PluginTemplates.convertDate(date, "DD MMMM YYYY");
                    isWhite = !isWhite;
                    day += 1;
                    const backGround: string = isWhite ? "" : "light-blue-background";
                    const history: string = day >= 4 ? "display-none" : "";
                    return (
                        <tr key={index}
                            className={`${backGround} ${history} ${showHistory ? "display-block" : ""}`}>
                            <td id="document-date" className="muli-semi-bold-16 pt-3">
                                <span
                                    className="font-weight-extra-light fs-14 ml-2">{PluginTemplates.convertDate(date, "DD MMMM YYYY")}</span>
                                <div className={"row ml-2"}>
                                    <h5 className={"col-10 pt-3 pl-0"}
                                        onClick={this.downloadDocument(document)}>{title}</h5>
                                    <a className="col-2 pl-1 document-download-link document-download-link-documents-page"
                                       onClick={this.downloadDocument(document)} id={trimFileExtension(name)}>
                                        <img className="active-policy-download-icon" alt="Document download icon"
                                             src="/img/Flow-download.svg"/>
                                    </a>
                                </div>
                            </td>
                        </tr>
                    )
                }

            });
        } else {
            return [];
        }
        this.setState({day});
        return documents;
    }

    private getRenewalDocumentsForPolicy(documents: Document[], renewalProposal?: Renewal): {
        currentRenewalDocuments: Document[];
        archivedRenewalInvites: Document[];
    } {
        let archiveInvitePdfs: Document[];

        if (renewalProposal) {
            const currentRenewalTag = `renewal_${renewalProposal._id}`;

            const renewalDocs = documents.filter(doc =>
                doc.tags.includes(currentRenewalTag)
            );

            const renewalPdfs = renewalDocs.filter(doc =>
                doc.name.includes(WOLF_RENEWAL_CLAIMS_IMPACT)
                || doc.name.includes(CAR_WOLF_RENEWAL_NCD_PROOF));

            const invitePdfs = renewalDocs.filter(doc =>
                doc.name.includes(WOLF_RENEWAL_INVITE_PACK));

            // if there are several invites per id, return the latest
            if (invitePdfs.length > 0) {
                const latestInvitePdf = [...invitePdfs].sort((a, b) =>
                    moment(b.date).diff(moment(a.date)))[0];
                renewalPdfs.push(latestInvitePdf);
            }

            archiveInvitePdfs = documents.filter(doc =>
                doc.name.includes(WOLF_RENEWAL_INVITE_PACK)
                && !doc.tags.includes(currentRenewalTag)
            );

            return {
                currentRenewalDocuments: [...renewalPdfs].reverse(),
                archivedRenewalInvites: this.keepLatestDocumentPerRenewalId(archiveInvitePdfs)
            };
        }

        archiveInvitePdfs = documents.filter(doc => doc.name.includes(WOLF_RENEWAL_INVITE_PACK));

        return {
            currentRenewalDocuments: [],
            archivedRenewalInvites: this.keepLatestDocumentPerRenewalId(archiveInvitePdfs)
        };
    }

    private keepLatestDocumentPerRenewalId(documents: Document[]): Document[] {
        const latestByRenewalId = new Map<string, Document>();

        documents.forEach(doc => {
            const renewalTag = this.getRenewalIdTag(doc);

            if (!renewalTag) {
                return;
            }

            const latestDocForThisRenewal = latestByRenewalId.get(renewalTag);

            if (!latestDocForThisRenewal || moment(doc.date).isAfter(moment(latestDocForThisRenewal.date))) {
                latestByRenewalId.set(renewalTag, doc);
            }
        });
        return Array.from(latestByRenewalId.values());
    }

    private getRenewalIdTag(doc: Document): string | undefined {
        return doc.tags.find((tag: string) =>
            /^renewal_[a-fA-F0-9]{24}$/.test(tag)
        );
    }
}

export const AnnualDocumentsPage = withRouter<{}, IDocumentsPageParams>(AnnualDocumentsPagePlain)
